import { useRef } from 'react'

import Box from '@ilg-totallymoney/ui/components/Box'
import Flex from '@ilg-totallymoney/ui/components/Flex'
import Heading from '@ilg-totallymoney/ui/components/Heading'
import theme from '@ilg-totallymoney/ui/theme'

import UnscoredButton from '../UnscoredButton'
import {
    StyledNextAnchor,
    StyledRowScroller,
} from './UnscoredCategoryPageNavigation.styled'
import useHighlightIntersectingNavigationItem from './hooks/useHighlightIntersectingNavigationItem'
import useStickyNavigationBar from './hooks/useStickyNavigationBar'
import { navigationItems } from './lib/navigationItems'

const UnscoredCategoryPageNavigation = () => {
    const navRef = useRef<HTMLDivElement>(null)

    const isSticky = useStickyNavigationBar(navRef)
    const { activeSection, scrollContainerRef, setNavItemRef } =
        useHighlightIntersectingNavigationItem()

    return (
        <Box
            maxWidth={theme.maxContentWidth}
            mx="auto"
            px={['space10', 'space60']}
        >
            <Box
                ref={navRef}
                boxShadow={isSticky ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none'}
                backgroundColor={isSticky ? 'white' : 'grey10'}
                borderRadius={[isSticky ? 0 : 'borderRadius40']}
                position={isSticky ? 'fixed' : 'relative'}
                top={0}
                left={0}
                right={0}
                zIndex={isSticky ? 99999 : 1}
                style={{
                    transition: 'all 0.3s ease-in-out',
                }}
                mx={isSticky ? 0 : 'space20'}
            >
                {isSticky ? (
                    <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        p="space10"
                        px={['space20', 'space40']}
                    >
                        <StyledRowScroller
                            ref={scrollContainerRef}
                            space="space30"
                            flexWrap="nowrap"
                        >
                            {navigationItems.map(({ href, id, name }, i) => (
                                <Box
                                    key={`${id}-${i}`}
                                    ref={el => setNavItemRef(el, id)}
                                    backgroundColor={
                                        activeSection === id
                                            ? 'neutral'
                                            : 'transparent'
                                    }
                                    p="space15"
                                    borderRadius="borderRadius20"
                                >
                                    <StyledNextAnchor
                                        href={href}
                                        style={{
                                            whiteSpace: 'nowrap',
                                            position: 'relative',
                                        }}
                                        onClick={e => {
                                            e.preventDefault()
                                            const element =
                                                document.getElementById(id)
                                            if (element) {
                                                element.scrollIntoView({
                                                    behavior: 'smooth',
                                                })
                                            }
                                        }}
                                    >
                                        {name}
                                    </StyledNextAnchor>
                                </Box>
                            ))}
                        </StyledRowScroller>

                        <Box display={['none', 'flex']} pl="space30">
                            <UnscoredButton
                                text="Find a card"
                                variant="secondaryOutline"
                                maxWidth="209px"
                            />
                        </Box>
                    </Flex>
                ) : (
                    <Flex
                        flexDirection={['column', 'row']}
                        alignItems={['flex-start', 'center']}
                        justifyContent={['flex-start', 'space-between']}
                        p={['space20', 'space10']}
                        px={['space20', 'space40']}
                    >
                        <Box display={['block', 'none']} mb="space20">
                            <Heading variant="40">On this page</Heading>
                        </Box>

                        <StyledRowScroller
                            space={['space20', 'space30']}
                            p="space10"
                            direction={['column', 'row']}
                            overflow={['none', 'scroll']}
                        >
                            {navigationItems.map(({ id, href, name }, i) => (
                                <Box key={`${id}-${i}`}>
                                    <StyledNextAnchor
                                        href={href}
                                        style={{
                                            whiteSpace: 'nowrap',
                                            position: 'relative',
                                        }}
                                        onClick={e => {
                                            e.preventDefault()
                                            const element =
                                                document.getElementById(id)
                                            if (element) {
                                                element.scrollIntoView({
                                                    behavior: 'smooth',
                                                })
                                            }
                                        }}
                                    >
                                        {name}
                                    </StyledNextAnchor>
                                </Box>
                            ))}
                        </StyledRowScroller>

                        <Box display={['none', 'flex']} pl="space30">
                            <UnscoredButton
                                text="Find a card"
                                variant="secondaryOutline"
                                maxWidth="209px"
                            />
                        </Box>
                    </Flex>
                )}

                <Box
                    display={isSticky ? ['flex', 'none'] : 'none'}
                    position="fixed"
                    boxShadow="0 -8px 16px rgba(0, 0, 0, 0.1)"
                    bottom={0}
                    left={0}
                    right={0}
                    p="space15"
                    backgroundColor="white"
                    zIndex={10}
                    justifyContent="center"
                >
                    <UnscoredButton
                        text="Find a card"
                        variant="secondaryOutline"
                    />
                </Box>
            </Box>
        </Box>
    )
}

export default UnscoredCategoryPageNavigation
